import { ChangeEvent, FC, useEffect, useState } from "react";
import Modal from "react-modal";
import close from "assets/images/close.svg";
import logo from "assets/images/modal-logo.svg";
import Button from "components/particles/primary-button";
import { RetailerDTO } from "utils/helpers/models/retailer.dto";
import { useForm, Controller } from "react-hook-form";
import FormErrorMessage from "components/particles/forms/form-error-message";
import { Container, StyledReactModal } from "./style";
import img from "assets/images/input-img-icon.svg";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import { setModalClosed } from "store/user-slice";
import useRetailers from "containers/retailer/listing/useHook";
import Swal from "sweetalert2";
import cam from "assets/images/upload-cam.svg";
import upload from "assets/images/upload-arrow.svg";
import deleteIcon from "assets/images/delete.svg";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { parsePhoneNumber } from "react-phone-number-input";
import GoogleMapPicker from "./GoogleMapPicker";

interface RetailerModelProps {
  refreshRetailers: () => void;
  retailer: any | null;
  updateList: Function
}

const RetailerPopup: FC<RetailerModelProps> = ({ refreshRetailers, retailer, updateList }) => {
  const { postRetailer, getStates, updateRetailer } = useRetailers();
  const [selectedCoverImage, setSelectedCoverImage] = useState<string | null>(
    null
  );
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { addRetailerModalIsOpen } = useSelector((state: any) => state.user);

  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [isMapPickerOpen, setIsMapPickerOpen] = useState(false);
  const [pendingCity, setPendingCity] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    formState: { errors, isValid },
    watch,
  } = useForm<RetailerDTO & { phoneNumber: string; longitude?: number; latitude?: number }>({
    mode: "onTouched",
    defaultValues: {
      state: "",
      city: "",
      phoneNumber: "",
      longitude: undefined,
      latitude: undefined,
    },
  });


  const openingTime = watch("openingTime");
  const closingTime = watch("closingTime");

  useEffect(() => {
    if (retailer) {
      setValue("firstName", retailer.firstName || "");
      setValue("lastName", retailer.lastName || "");
      setValue("title", retailer.roleInStore || "");
      setValue("email", retailer.email || "");
      setValue("companyName", retailer.companyName || "");
      let phoneNumber = "";
      if (retailer.countryCode && retailer.mobileNumber) {
        const countryCode = retailer.countryCode.toString().replace(/^\+/, "");
        phoneNumber = `+${countryCode}${retailer.mobileNumber}`;
      }
      setValue("phoneNumber", phoneNumber);
      setValue("openingTime", retailer.openingTime || "");
      setValue("closingTime", retailer.closingTime || "");
      setValue("websiteUrl", retailer.websiteUrl || "");
      setValue("physicalAddress", retailer.physicalAddress || "");
      setValue("state", retailer.state || "");
      setValue("city", retailer.city || "");
      setValue("zipCode", retailer.zipCode || "");
      setValue("googleLocationUrl", retailer.googleLocationUrl || "");
      setValue("longitude", retailer.longitude || undefined);
      setValue("latitude", retailer.latitude || undefined);

      if (retailer.image) setSelectedImage(retailer.image);
      if (retailer.coverImage) setSelectedCoverImage(retailer.coverImage);
    } else {
      reset();
      setValue("phoneNumber", "");
    }
  }, [retailer, reset, setValue]);


  useEffect(() => {
    const fetchStates = async () => {
      try {
        const [usaResponse, indiaResponse] = await Promise.all([
          fetch("assets/data/states.json"),
          fetch("assets/data/india.json")
        ]);

        const usaData = await usaResponse.json();
        const indiaData = await indiaResponse.json();

        const mergedStates = [
          ...(usaData.states || []),
          ...(indiaData.states || [])
        ];

        setStates(mergedStates);
      } catch (error) {
        console.error("Error fetching states:", error);
        try {
          const response = await getStates();
          const data = await response.json();
          setStates(data.states || []);
        } catch (fallbackError) {
          console.error("Error fetching states from API:", fallbackError);
          setStates([]);
        }
      }
    };

    fetchStates();
  }, []);

  const selectedState = watch("state");

  useEffect(() => {
    const filteredCities =
      states.find((state) => state.name === selectedState)?.cities || [];
    setCities(filteredCities);

    if (!retailer && selectedState && !pendingCity) {
      setValue("city", "");
    }
  }, [selectedState, states, setValue, retailer, pendingCity]);

  useEffect(() => {
    if (pendingCity) {
      if (cities.length > 0 && selectedState) {
        console.log("Attempting to match city:", pendingCity);
        console.log("Available cities:", cities.map((c: any) => c.name));
        console.log("Selected state:", selectedState);

        let matchedCity = cities.find(
          (city: { id: string; name: string }) =>
            city.name.toLowerCase().trim() === pendingCity.toLowerCase().trim()
        );

        if (!matchedCity) {
          matchedCity = cities.find(
            (city: { id: string; name: string }) =>
              city.name.toLowerCase().trim().includes(pendingCity.toLowerCase().trim()) ||
              pendingCity.toLowerCase().trim().includes(city.name.toLowerCase().trim())
          );
        }

        const cityToSet = matchedCity ? matchedCity.name : pendingCity;

        setTimeout(() => {
          setValue("city", cityToSet, { shouldValidate: true, shouldDirty: true });
          if (matchedCity) {
            console.log("✅ City matched and set:", matchedCity.name, "from pending:", pendingCity);
          } else {
            console.log("⚠️ City set without match:", pendingCity, "Available cities:", cities.map((c: any) => c.name));
          }
          setPendingCity(null);
        }, 150);
      } else {
        console.log("⏳ Waiting for cities to be filtered. Pending city:", pendingCity, "Cities count:", cities.length, "Selected state:", selectedState);

        if (selectedState && cities.length === 0) {
          const timeoutId = setTimeout(() => {
            const filteredCities = states.find((state) => state.name === selectedState)?.cities || [];
            if (filteredCities.length > 0 && pendingCity) {
              console.log("Retrying city match after delay. Cities now available:", filteredCities.length);
            }
          }, 200);
          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [cities, pendingCity, selectedState, setValue, states]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedImage(URL.createObjectURL(file));
      setValue("image", file);
    }
  };
  const handleCoverImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const files = event.target.files[0];
      setSelectedCoverImage(URL.createObjectURL(files));
      setValue("coverImage", files);
    }
  };

  const handleLocationSelect = (address: string, lat: number, lng: number, googleMapUrl: string, addressComponents?: { state?: string; city?: string; zipCode?: string }) => {
    console.log("📍 Location selected. Address components:", addressComponents);

    setValue("physicalAddress", address);
    setValue("latitude", lat);
    setValue("longitude", lng);
    setValue("googleLocationUrl", googleMapUrl);

    if (addressComponents) {
      if (addressComponents.state) {
        console.log("🔍 Looking for state:", addressComponents.state, "in states:", states.map((s: any) => s.name));

        const matchedState = states.find(
          (state) => state.name.toLowerCase().trim() === addressComponents.state!.toLowerCase().trim()
        );

        if (matchedState) {
          console.log("✅ State matched:", matchedState.name);
          if (addressComponents.city) {
            console.log("📝 Setting pending city:", addressComponents.city);
            setPendingCity(addressComponents.city);
          }
          setValue("state", matchedState.name, { shouldValidate: true, shouldDirty: true });
        } else {
          console.log("⚠️ State not matched, using:", addressComponents.state);
          if (addressComponents.city) {
            console.log("📝 Setting pending city:", addressComponents.city);
            setPendingCity(addressComponents.city);
          }
          setValue("state", addressComponents.state, { shouldValidate: true, shouldDirty: true });
        }
      } else if (addressComponents.city) {
        console.log("📝 Setting city directly (no state):", addressComponents.city);
        setValue("city", addressComponents.city, { shouldValidate: true, shouldDirty: true });
      }

      if (addressComponents.zipCode) {
        setValue("zipCode", addressComponents.zipCode, { shouldValidate: true, shouldDirty: true });
      }
    }
  };

  const getInitialLocation = () => {
    const lat = watch("latitude");
    const lng = watch("longitude");

    if (lat && lng) {
      return { lat: Number(lat), lng: Number(lng) };
    }

    const googleLocationUrl = watch("googleLocationUrl");
    if (googleLocationUrl) {
      const match = googleLocationUrl.match(/[?&]q=([+-]?\d+\.?\d*),([+-]?\d+\.?\d*)/);
      if (match && match[1] && match[2]) {
        const extractedLat = parseFloat(match[1]);
        const extractedLng = parseFloat(match[2]);
        if (!isNaN(extractedLat) && !isNaN(extractedLng)) {
          return { lat: extractedLat, lng: extractedLng };
        }
      }
    }

    return undefined;
  };

  const onSubmit = async (data: RetailerDTO & { phoneNumber: string; longitude?: number; latitude?: number }) => {
    const formData = new FormData();

    let countryCode = "";
    let mobileNumber = "";

    if (data.phoneNumber) {
      try {
        const phoneNumber = parsePhoneNumber(data.phoneNumber);
        if (phoneNumber) {
          countryCode = phoneNumber.countryCallingCode || "";
          mobileNumber = phoneNumber.nationalNumber || "";
        }
      } catch (error) {
        console.error("Error parsing phone number:", error);
      }
    }

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null && key !== "phoneNumber" && key !== "countryCode" && key !== "mobileNumber" && key !== "title" && key !== "longitude" && key !== "latitude") {
        formData.append(key, value as any);
      }
    });

    if (data.title) {
      formData.append("roleInStore", data.title);
    }

    if (countryCode) {
      formData.append("countryCode", countryCode);
    }
    if (mobileNumber) {
      formData.append("mobileNumber", mobileNumber);
    }

    if (data.longitude !== undefined && data.longitude !== null) {
      formData.append("longitude", data.longitude.toString());
    }
    if (data.latitude !== undefined && data.latitude !== null) {
      formData.append("latitude", data.latitude.toString());
    }

    try {
      if (retailer) {
        const updated = await updateRetailer(retailer.id, formData);

        if (updated) {
          updateList(updated);
        }
        Swal.fire("Success", "Retailer updated successfully", "success");
      } else {
        await postRetailer(formData);
        Swal.fire("Success", "Retailer added successfully", "success");
      }

      dispatch(setModalClosed("Retailer"));
      await refreshRetailers();
      reset();
      setSelectedCoverImage(null);
      setSelectedImage(null);
      setValue("image", null);
      setValue("coverImage", null);
      setValue("phoneNumber", "");
    } catch (error) {
      console.error(error);
      Swal.fire("Error", retailer ? "Failed to update retailer" : "Failed to add retailer", "error");
    }
  };

  const closeModal = () => {
    dispatch(setModalClosed("Retailer"));
    setSelectedImage(null);
    setSelectedCoverImage(null);
    setValue("image", null);
    setValue("coverImage", null);
    setValue("phoneNumber", "");
    reset();
  };

  const closeImg = () => {
    setSelectedImage(null);
    setValue("image", null);
  };
  const closeCoverImg = () => {
    setSelectedCoverImage(null);
    setValue("coverImage", null);
  };

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      fontSize: "2rem",
      width: "68.5rem",
      borderRadius: "3rem",
      padding: "0",
    },
  };

  return (
    <StyledReactModal
      isOpen={addRetailerModalIsOpen}
      onRequestClose={closeModal}
      contentLabel="Add a New Retailer"
    >
      <Container onSubmit={handleSubmit(onSubmit)}>

        <div className="model-header" style={{ position: "relative" }}>
          <div className="model-title">
            <h1 className="tag-line-banner">
              {retailer ? "Edit Retailer" : "Add a New Retailer"}
            </h1>
            <button className="close-button" onClick={closeModal}>
              <img src={close} alt="Close" />
            </button>
          </div>
          {/* / cover Img / */}

          {selectedCoverImage ? (
            <>
              <img
                style={{ width: "100%", height: "78%", objectFit: "cover" }}
                src={selectedCoverImage}
                alt="Selected"
              />
              <img
                className="close-icon"
                style={{ top: "110px", right: "30px" }}
                src={deleteIcon}
                alt="Remove"
                onClick={closeCoverImg}
              />
            </>
          ) : (
            <>
              <label
                htmlFor="image-cover-input"
                style={{
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "end",
                  alignItems: "center",
                  paddingTop: "50px",
                  paddingRight: "20px",
                  gap: "1rem",
                }}
              >
                <img src={upload} className="upload-icon" alt="" />
                <p>Upload Cover</p>
              </label>
              <input
                id="image-cover-input"
                type="file"
                accept="image/*"
                className="hidden-input"
                {...register("coverImage")}
                onChange={handleCoverImageChange}
              />
            </>
          )}

          {/* / Logo / */}
          <div className="logo-main" style={{ position: "unset" }}>
            {selectedImage ? (
              <div
                className="modal-logo"
                style={{ position: "absolute", top: "63px" }}
              >
                <img
                  src={selectedImage}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              </div>
            ) : (
              <div
                className="modal-logo"
                style={{ position: "absolute", top: "63px" }}
              >
                <img src={logo} alt="" />
              </div>
            )}

            <label htmlFor="image-input">
              {selectedImage ? (
                <img
                  src={selectedImage}
                  className="uploaded-image"
                  alt="Selected"
                  style={{ top: "140px" }}
                />
              ) : (
                <img
                  src={cam}
                  className="upload-cam"
                  alt=""
                  style={{ top: "140px", cursor: "pointer" }}
                />
              )}
            </label>
            {selectedImage ? (
              <>
                <img
                  className="uploaded-image"
                  src={selectedImage}
                  alt="Selected"
                />
                <img
                  className="close-icon"
                  src={deleteIcon}
                  alt="Remove"
                  onClick={closeImg}
                  style={{
                    top: "150px",
                    left: "55%",
                    cursor: "pointer",
                    backgroundColor: "red",
                    borderRadius: "50%",
                    padding: "5px",
                    width: "30px",
                    height: "30px",
                  }}
                />
              </>
            ) : (
              <>
                <label htmlFor="image-input">
                  <img src={img} alt="Upload" className="image-input" />
                </label>
                <input
                  id="image-input"
                  type="file"
                  accept="image/*"
                  className="hidden-input"
                  {...register("image")}
                  onChange={handleImageChange}
                />
              </>
            )}
          </div>
        </div>

        <div className="model-container">
          <div className="add-retailer-container">
            <div className="retailer-input">
              <label htmlFor="firstName">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                {...register("firstName", {
                  required: "First name is required",
                  maxLength: {
                    value: 30,
                    message: "First name cannot exceed 30 characters",
                  },
                })}
              />
              <FormErrorMessage error={errors?.firstName} />

            </div>

            <div className="retailer-input">
              <label htmlFor="lastName">
                Last Name
              </label>
              <input
                id="lastName"
                type="text"
                {...register("lastName", {
                  required: "Last name is required",
                  maxLength: {
                    value: 30,
                    message: "Last name cannot exceed 30 characters",
                  },
                })}
              />
              <FormErrorMessage error={errors?.lastName} />

            </div>
            <div className="retailer-input">
              <label htmlFor="title">
                Title
              </label>
              <input
                id="title"
                type="text"
                {...register("title", {
                  required: "Title is required",
                  maxLength: {
                    value: 30,
                    message: "Title cannot exceed 30 characters",
                  },
                })}
              />
              <FormErrorMessage error={errors?.title} />

            </div>
            <div className="retailer-input">
              <label htmlFor="email">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Invalid email address",
                  },
                })}
              />
              <FormErrorMessage error={errors.email} />
            </div>
            <div className="retailer-input">
              <label htmlFor="companyName">
                Company Name
              </label>
              <input
                id="companyName"
                type="text"
                {...register("companyName", {
                  required: "Company name is required",
                  maxLength: {
                    value: 60,
                    message: "Company name cannot exceed 60 characters",
                  },
                })}
              />
              <FormErrorMessage error={errors?.companyName} />

            </div>
            <div className="retailer-input">
              <label htmlFor="phoneNumber">
                Mobile Number
              </label>
              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  required: "Mobile number is required",
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    id="phoneNumber"
                    international
                    defaultCountry="US"
                    value={value}
                    onChange={onChange}
                    className="phone-input"
                    style={{
                      width: "100%",
                    }}
                  />
                )}
              />
              <FormErrorMessage error={errors?.phoneNumber as any} />
            </div>


            <div className="retailer-input">
              <label htmlFor="openingTime">
                Pickup - Start Time
              </label>
              <input
                id="openingTime"
                type="time"
                {...register("openingTime", {
                  required: "Opening time is required",
                  validate: (value) =>
                    !!!closingTime ||
                    value < closingTime ||
                    "Opening time must be before closing time",
                })}
              />
              <FormErrorMessage error={errors.openingTime} />
            </div>

            <div className="retailer-input">
              <label htmlFor="closingTime">
                Pick - End Time
              </label>
              <input
                id="closingTime"
                type="time"
                {...register("closingTime", {
                  required: "Closing time is required",
                  validate: (value) =>
                    !!!openingTime ||
                    value > openingTime ||
                    "Closing time must be later than opening time",
                })}
              />
              <FormErrorMessage error={errors.closingTime} />
            </div>
            <div className="retailer-input">
              <label htmlFor="websiteUrl">
                Website / Social Media
              </label>
              <input
                id="websiteUrl"
                type="text"
                {...register("websiteUrl", {
                  pattern: {
                    value: /^(https?:\/\/)?([\w\-]+\.)+[a-z]{2,}(\S*)?$/i,
                    message: "Invalid URL format",
                  },
                  required: false,
                })}
              />

            </div>
            <div className="retailer-input">
              <label htmlFor="physicalAddress">
                Store Address
              </label>
              <input
                id="physicalAddress"
                type="text"
                style={{ width: "100%", cursor: "pointer" }}
                {...register("physicalAddress", { required: true })}
                onClick={() => setIsMapPickerOpen(true)}
                readOnly
              />
              <FormErrorMessage error={errors.physicalAddress} />
            </div>

            <div className="retailer-input">
              <label htmlFor="state">
                State
              </label>
              <select
                id="state"
                className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                {...register("state", { required: "State is required" })}
              >
                <option value="" disabled>
                  Select State
                </option>
                {states.map((state, index) => (
                  <option key={index} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
              <FormErrorMessage error={errors.state} />
            </div>

            <div className="retailer-input">
              <label htmlFor="city">
                City
              </label>
              <select
                id="city"
                className="w-full h-[51px] px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none"
                {...register("city", { required: "City is required" })}
                disabled={!selectedState}
              >
                <option value="" disabled>
                  Select City
                </option>
                {cities.map((city: { id: string; name: string }) => (
                  <option key={city.id} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>

              <FormErrorMessage error={errors.city} />
            </div>
            <div className="retailer-input">
              <label htmlFor="zipCode">
                Zip Code
              </label>
              <input
                id="zipCode"
                type="number"
                min={0}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e') {
                    e.preventDefault();
                  }
                }}
                {...register("zipCode", {
                  required: "Zip Code is required",
                })}
              />
              <FormErrorMessage error={errors?.zipCode} />
            </div>


            <div className="retailer-input">
              <label htmlFor="googleLocationUrl">
                Map Location
              </label>
              <input
                id="googleLocationUrl"
                type="text"
                style={{ cursor: "pointer" }}
                {...register("googleLocationUrl", {
                  required: "Please select a location on the map",
                })}
                onClick={() => setIsMapPickerOpen(true)}
                readOnly
              />
              <FormErrorMessage error={errors.googleLocationUrl} />
            </div>

          </div>

          <div className="add-button">
            <button className="submit-btn" type="submit">
              {retailer ? "Update" : "Add"}
            </button>
          </div>
        </div>

      </Container>

      <GoogleMapPicker
        isOpen={isMapPickerOpen}
        onClose={() => setIsMapPickerOpen(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={getInitialLocation()}
        initialAddress={watch("physicalAddress")}
      />
    </StyledReactModal>
  );
};

export default RetailerPopup;
